import { isDefined, isNotDefined, makeValuesPredicate } from '@sgme/fp';
import { BatchForm } from 'components/batchForm/BatchForm';
import { BatchFormFieldType, buildSingleCellId } from 'components/batchForm/BatchForm.helpers';
import {
  clippingWithGrid,
  nextCellCoordinates,
} from 'components/common-form/builder/form/node/field/Grid/GridField.helper';
import {
  CellCoordinates,
  CellMetaData,
  Navigation,
} from 'components/common-form/builder/form/node/field/Grid/GridField.model';
import {
  BatchDragAndDropContext,
  BatchDragAndDropContextValue,
} from 'components/common-form/contexts/batch/BatchDragAndDropContext';
import type { BatchNavigationContextValue } from 'components/common-form/contexts/batch/BatchNavigationContext';
import { BatchNavigationContext } from 'components/common-form/contexts/batch/BatchNavigationContext';
import {
  BatchPasteDataContextProvider,
  BatchPasteDataContextValue,
  FailedPasteCell,
} from 'components/common-form/contexts/batch/BatchPasteDataContext';
import { FormLoadingIndicator } from 'components/shared/FormLoadingIndicator/FormLoadingIndicator';
import { useCellMetadata } from 'components/Workspaces/Batch/FormGrid/use-cell-metadata';
import { useOnClickOutside } from 'hooks/useOnClickOutside';
import { useUserNotification } from 'hooks/useUserNotification';
import { DragEvent, FC, Fragment, KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import type { ProductGraph } from 'state/product-description/model';
import styled from 'styled-components';

import { connectedBatchFormGrid } from './BatchFormGrid.connect';
import type { BatchFormGridProps } from './BatchFormGrid.models';
import { BatchBlockNameColumn } from './FieldNameColumn/BlockNameColumn';
import { BatchFieldNameColumn } from './FieldNameColumn/FieldNameColumn';
import { BatchFormHeaderCell } from './FormHeaderCell/FormHeaderCell';
import { BatchGridActionHeader } from './GridActionHeader/GridActionHeader';

const isListField = makeValuesPredicate(['ListField', 'CurrencyListField']);

export const StyledGrid = styled.div<{ $nbRows: number; $nbColumns: number }>`
  display: grid;
  grid-auto-flow: column;
  grid-template-rows: ${({ $nbRows }) => `auto repeat(${$nbRows}, minmax(32px, auto))`};
  grid-template-columns: ${({ $nbColumns }) =>
    `auto minmax(auto, 204px) repeat(${$nbColumns <= 1 ? 1 : $nbColumns}, 216px)`};
  width: max-content;
`;

export const BatchFormGridComponent: FC<BatchFormGridProps> = ({
  className,
  tabId,
  productDescription,
  formIds,
  productType,
  productSubtype,
  wrapper,
  payoffTypes,
  defaultUnderlyingCodeType,
}) => {
  const workSpaceString = 'Batch Workspace';
  const arrowKeyEventString = 'Navigate with arrow key (Arrow key)';
  const userNotifier = useUserNotification();
  const formGridRef = useRef(null);

  const nbFields = useMemo(() => getNbFields(productDescription), [productDescription]);

  const gridRefs = useRef<Record<string, CellMetaData>>({});

  const addGridRef = useCallback((cellId: string, cellMetaData: CellMetaData) => {
    gridRefs.current[cellId] = cellMetaData;
  }, []);
  const [selectedCell, setSelectedCell] = useState<CellCoordinates | undefined>(undefined);
  const selectedCellId = useMemo(() => {
    return isDefined(selectedCell) ? buildSingleCellId(selectedCell.row, selectedCell.column) : undefined;
  }, [selectedCell]);

  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isSelectAll, setIsSelectAll] = useState<boolean>(false);

  const [dragHandleCellId, setDragHandleCellId] = useState<string | undefined>(undefined);
  const [dragCell, setDragCell] = useState<CellCoordinates | undefined>(undefined);
  const [dragHoveredCellIds, setDragHoveredCellIds] = useState<string[] | undefined>(undefined);
  const [draggedCellIdsForCopy, setDraggedCellIdsForCopy] = useState<string[] | undefined>(undefined);
  const [originalDraggedValue, setOriginalDraggedValue] = useState<BatchFormFieldType | undefined>(undefined);

  const [pasteCellId, setPasteCellId] = useState<string | undefined>(undefined);
  const [pastedGridData, setPastedGridData] = useState<Record<string, string> | undefined>(undefined);
  const [pastedData, setPastedData] = useState<string | undefined>(undefined);
  const [failedPasteCells, setFailedPasteCells] = useState<FailedPasteCell[] | undefined>(undefined);
  const selectedCellMetadata = useCellMetadata(selectedCell, formIds, productDescription);
  const onNavigated = useCallback(
    (navigation: Navigation) => {
      const clipping = clippingWithGrid({
        gridId: '',
        nbRows: nbFields,
        nbColumns: formIds.length,
      });
      if (isNotDefined(selectedCell)) {
        return;
      }
      const nextCoordinates = nextCellCoordinates(selectedCell, navigation, clipping);
      setSelectedCell({ row: nextCoordinates.row, column: nextCoordinates.column });
    },
    [formIds.length, nbFields, selectedCell],
  );

  const onNavigationKeyDown = useCallback(
    (e: KeyboardEvent, canEdit: boolean) => {
      switch (e.key) {
        case 'Escape':
          setIsEditing(false);
          e.preventDefault();
          break;
        case 'Enter':
          if (!canEdit) {
            onNavigated('down');
            break;
          }
          setIsSelectAll(true);
          setIsEditing(true);
          e.preventDefault();
          break;
        case ' ':
          if (!canEdit) {
            break;
          }
          logAnalytics.trackEvent(workSpaceString, 'Edit field (Space)');
          setIsSelectAll(true);
          setIsEditing(true);
          e.preventDefault();
          break;
        case 'ArrowLeft':
          logAnalytics.trackEvent(workSpaceString, arrowKeyEventString);
          onNavigated('left');
          e.preventDefault();
          break;
        case 'ArrowRight':
          logAnalytics.trackEvent(workSpaceString, arrowKeyEventString);

          onNavigated('right');
          e.preventDefault();
          break;
        case 'ArrowUp':
          logAnalytics.trackEvent(workSpaceString, arrowKeyEventString);

          onNavigated('up');
          e.preventDefault();
          break;
        case 'ArrowDown':
          logAnalytics.trackEvent(workSpaceString, arrowKeyEventString);

          onNavigated('down');
          e.preventDefault();
          break;
        case 'Tab':
          logAnalytics.trackEvent(workSpaceString, 'Navigate horizontally (Tab)');
          if (e.shiftKey) {
            onNavigated('previous');
          } else {
            onNavigated('next');
          }
          e.preventDefault();
          break;
        default:
          break;
      }
    },
    [onNavigated],
  );

  const onEditingKeyDown = useCallback(
    (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          setIsEditing(false);
          if (isDefined(selectedCellId)) {
            gridRefs.current[selectedCellId].el.focus();
          }
          break;
        case 'Enter':
          logAnalytics.trackEvent(workSpaceString, 'Confirm field value (Enter)');
          e.preventDefault();
          setIsEditing(false);
          onNavigated('down');
          break;
        case 'Tab':
          logAnalytics.trackEvent(workSpaceString, 'Navigate horizontally (Tab)');
          e.preventDefault();
          setIsEditing(false);
          onNavigated('next');
          break;
        default:
          break;
      }
    },
    [onNavigated, selectedCellId],
  );

  const onEditionCompleted = useCallback(() => {
    setIsEditing(false);
    onNavigated('down');
  }, [onNavigated]);

  const onDragStart = useCallback((e: DragEvent, rowIndex: number, columnIndex: number) => {
    const img = new Image();
    img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAUEBAAAACwAAAAAAQABAAACAkQBADs=';
    e.dataTransfer.setDragImage(img, 0, 0);
    setDragCell({ row: rowIndex, column: columnIndex });
    setDraggedCellIdsForCopy([buildSingleCellId(rowIndex, columnIndex)]);
  }, []);

  const onDragOver = useCallback(
    (e: DragEvent, rowIndex: number, columnIndex: number) => {
      e.preventDefault();
      if (isNotDefined(setDragHoveredCellIds)) {
        return;
      }
      if (dragCell && dragCell.row === rowIndex) {
        setDragHoveredCellIds([...(dragHoveredCellIds ?? []), buildSingleCellId(rowIndex, columnIndex)]);
      } else {
        setDragHoveredCellIds([]);
      }
    },
    [dragCell, dragHoveredCellIds],
  );

  const onDrop = useCallback(
    (e: DragEvent, rowIndex: number) => {
      e.preventDefault();
      if (isNotDefined(dragHoveredCellIds) || isNotDefined(setDraggedCellIdsForCopy)) {
        return;
      }
      setDragHandleCellId(dragHoveredCellIds[dragHoveredCellIds.length - 1]);

      if (dragCell && dragCell.row === rowIndex) {
        setDraggedCellIdsForCopy(dragHoveredCellIds);
      } else {
        setDraggedCellIdsForCopy(undefined);
      }
      setDragHoveredCellIds([]);
    },
    [dragHoveredCellIds, dragCell],
  );

  const batchNavigationContextValue = useMemo<BatchNavigationContextValue>(() => {
    return {
      isEditing,
      setIsEditing,
      setSelectedCell,
      onNavigationKeyDown,
      onEditingKeyDown,
      addGridRef,
      selectedCellId,
      onEditionCompleted,
      isSelectAll,
      setIsSelectAll,
      defaultUnderlyingCodeType,
    };
  }, [
    isEditing,
    onNavigationKeyDown,
    onEditingKeyDown,
    addGridRef,
    selectedCellId,
    onEditionCompleted,
    isSelectAll,
    defaultUnderlyingCodeType,
  ]);

  const batchDragAndDropContextValue = useMemo<BatchDragAndDropContextValue>(() => {
    return {
      dragHandleCellId,
      dragHoveredCellIds,
      draggedCellIdsForCopy,
      onDragStart,
      onDragOver,
      onDrop,
      originalDraggedValue,
      setDragHandleCellId,
      setOriginalDraggedValue,
      setDragHoveredCellIds,
      setDraggedCellIdsForCopy,
    };
  }, [
    dragHandleCellId,
    dragHoveredCellIds,
    draggedCellIdsForCopy,
    onDragStart,
    onDragOver,
    onDrop,
    originalDraggedValue,
  ]);

  const batchPasteDataContextValue = useMemo<BatchPasteDataContextValue>(() => {
    return {
      pasteCellId,
      setPasteCellId,
      pastedGridData,
      setPastedGridData,
      failedPasteCells,
      setFailedPasteCells,
      pastedData,
      setPastedData,
    };
  }, [pasteCellId, pastedGridData, failedPasteCells, pastedData]);

  useEffect(() => {
    setIsSelectAll(false);
    if (
      isDefined(selectedCellId) &&
      isDefined(gridRefs.current[selectedCellId]) &&
      !isListField(gridRefs.current[selectedCellId].type)
    ) {
      if (
        selectedCellMetadata?.type !== 'MultipleUnderlyingsField' &&
        selectedCellMetadata?.type !== 'UnderlyingsField'
      ) {
        gridRefs.current[selectedCellId].el.focus();
      }
      setDragHoveredCellIds(undefined);
      setDraggedCellIdsForCopy(undefined);

      return () => {
        setOriginalDraggedValue(undefined);
      };
    }
  }, [selectedCellId, selectedCellMetadata]);

  useEffect(() => {
    if (
      isNotDefined(failedPasteCells) ||
      failedPasteCells.length === 0 ||
      isNotDefined(pastedGridData) ||
      Object.keys(pastedGridData).length !== 0
    ) {
      return;
    }
    failedPasteCells.forEach(({ cellId, fieldId, pastedValue }) => {
      const formNumber = cellId.split('-').map(index => parseInt(index))[1] + 1;
      const notificationContent = `The value "${pastedValue}" pasted for the "${fieldId}" field in form ${formNumber} does not match with the expected data type or format.`;
      userNotifier.error({ title: { id: 'toast.paste' }, content: [notificationContent] });
    });

    setFailedPasteCells([]);
  }, [failedPasteCells, pastedGridData, userNotifier]);

  useOnClickOutside(formGridRef, () => setSelectedCell(undefined));

  if (!productDescription) {
    return <FormLoadingIndicator className="w-100" />;
  }

  return (
    <div className={className} data-e2e="batch-form-grid" role="grid" aria-label="Batch forms grid" ref={formGridRef}>
      <StyledGrid className="bg-lvl2" $nbRows={nbFields} $nbColumns={formIds.length}>
        <BatchGridActionHeader
          className="batch-grid-cell is-sticky-left with-border-bottom-thick"
          productSubtype={productSubtype}
          productType={productType}
          wrapper={wrapper}
          payoffType={payoffTypes}
          tabId={tabId}
        />
        <BatchBlockNameColumn productDescription={productDescription} />
        <BatchFieldNameColumn productDescription={productDescription} />
        {formIds.map((formId, formIndex) => (
          <Fragment key={formId}>
            <BatchFormHeaderCell
              tabId={tabId}
              formId={formId}
              columnIndex={formIndex}
              productType={productType}
              productSubtype={productSubtype}
              wrapper={wrapper}
              e2e="batch-form-header"
              role="columnheader"
              ariaColIndex={formIndex + 1}
              ariaRowIndex={1}
              ariaLabel="Status"
              data-testid={`batch-form-header-status-${formIndex}`}
            />
            <BatchNavigationContext.Provider value={batchNavigationContextValue}>
              <BatchDragAndDropContext.Provider value={batchDragAndDropContextValue}>
                <BatchPasteDataContextProvider value={batchPasteDataContextValue}>
                  <BatchForm
                    tabId={tabId}
                    formId={formId}
                    productDescription={productDescription}
                    formIndex={formIndex}
                  />
                </BatchPasteDataContextProvider>
              </BatchDragAndDropContext.Provider>
            </BatchNavigationContext.Provider>
          </Fragment>
        ))}
      </StyledGrid>
    </div>
  );
};

export const BatchFormGrid = connectedBatchFormGrid(BatchFormGridComponent);

function getNbFields(productGraph?: ProductGraph) {
  let count = 0;
  productGraph?.forEach(blockNode => blockNode.children?.forEach(() => count++));
  return count;
}
